import React, { useMemo, useEffect, useState, Fragment } from 'react';
import { Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import _isEqual from 'lodash/isEqual';
import { isSameDay, format } from 'date-fns';
import { RANGES } from 'components/DateRangePicker/ranges';

import LoaderBar from 'components/LoaderBar';
import Table from 'components/Table';
import DashboardContainer from 'components/DashboardContainer';
import TableTopFilters from 'components/TableTopFilters';
import StatusBadge from 'components/StatusBadge';
import SearchWithFilters from 'components/SearchWithFilters';
import TransactionActions from './TransactionActions';

import { DATE_FOR_API, MONTH_DAY_HOUR } from 'utils/dateFormat';
import { usePrevious } from 'hooks';
import { STATUS_FILTERS, SEARCH_FILTERS } from './helpers';
import schema, { FIELD_NAMES, defaultValues as _defaultValues } from './schema';
import { transactionsTable as TEXT } from 'locales/en/transaction';
import {
  transactionAllSelector,
  getTransactions,
  searchTransactions,
  setParams,
  setSearchFilter,
  setSearchTerm,
  exportTransactions,
  resetSearch,
} from 'redux/slices/transaction';

const Transactions = () => {
  const [activeItem, setActiveItem] = useState(null);

  const dispatch = useDispatch();
  const { data, params, pagination, isLoading, searchTerm, searchFilter } =
    useSelector((state) => transactionAllSelector(state));
  const company = useSelector((state) => state.company.current);
  const prevCompany = usePrevious(company);
  const prevParams = usePrevious(params);
  const prevSearchFilter = usePrevious(searchFilter);

  useEffect(() => {
    dispatch(getTransactions());

    return () => {
      dispatch(resetSearch());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (prevCompany !== null && prevCompany !== company) {
      dispatch(
        setParams({
          page: 1,
        })
      );
      dispatch(getTransactions());
    }
  }, [company, dispatch, prevCompany]);

  useEffect(() => {
    if (
      (prevParams !== null && !_isEqual(params, prevParams)) ||
      (!!prevSearchFilter && searchFilter === undefined)
    ) {
      dispatch(getTransactions());
    }
  }, [dispatch, params, prevParams, prevSearchFilter, searchFilter]);

  const onApplyFilter = (data) => {
    const {
      statusCheckbox,
      status,
      amount_filter,
      amount,
      fraud_score_min,
      fraud_score_max,
    } = data;
    let statusSet = new Set();

    for (const property in status) {
      if (status[property]) {
        statusSet.add(property);
      }
    }
    const statusFilter = Array.from(statusSet).join(',');
    let params = {
      statusCheckbox,
      status: statusFilter,
      amount,
      amount_filter,
      fraud_score_min,
      fraud_score_max,
    };

    dispatch(setParams({ ...params, page: 1 }));
  };

  const onApplyDate = (date) => {
    dispatch(
      setParams({
        from: format(date.startDate, DATE_FOR_API),
        to: format(date.endDate, DATE_FOR_API),
        page: 1,
      })
    );
  };

  const onChangePerPage = (per_page) => {
    dispatch(
      setParams({
        per_page,
        page: 1,
      })
    );
  };

  const onChangePage = ({ selected }) => {
    dispatch(
      setParams({
        page: selected + 1,
      })
    );
  };

  const toggleActiveItem = (id) => {
    if (activeItem === id) {
      setActiveItem(null);
    } else {
      setActiveItem(id);
    }
  };
  const range = useMemo(() => {
    let id =
      RANGES.find(
        (item) =>
          isSameDay(new Date(item.range().startDate), new Date(params.from)) &&
          isSameDay(new Date(item.range().endDate), new Date(params.to))
      )?.range()?.id || RANGES[RANGES.length - 1]?.range()?.id;

    return {
      startDate: new Date(params.from),
      endDate: new Date(params.to),
      id,
      key: 'selection',
    };
  }, [params.from, params.to]);

  const onChangeSearchTerm = (event) => {
    dispatch(setSearchTerm(event.target.value || ''));
  };

  const onSelectSearchFilter = (item, { action }) => {
    if (action === 'clear') {
      dispatch(resetSearch());
    } else {
      dispatch(setSearchFilter(item));
    }
  };

  const onSearch = () => dispatch(searchTransactions());

  const onExport = () => dispatch(exportTransactions());

  return (
    <DashboardContainer header={TEXT.header}>
      <LoaderBar isVisible={isLoading} />
      <SearchWithFilters
        onChangeFilter={onSelectSearchFilter}
        onChangeInput={onChangeSearchTerm}
        searchTerm={searchTerm}
        searchFilter={searchFilter}
        options={SEARCH_FILTERS}
        onSearch={onSearch}
        hasFilters
        isClearable
      />
      <Table
        topContent={
          <TableTopFilters
            exportHeader={TEXT.export}
            onApplyFilter={onApplyFilter}
            statusFilter={STATUS_FILTERS}
            defaultValues={_defaultValues(params)}
            schema={schema}
            fieldNames={FIELD_NAMES}
            onApplyDate={onApplyDate}
            range={range}
            onConfirmExport={onExport}
            isExportDisabled={!data.length}
          />
        }
        hasData={!!data.length}
        titles={[
          TEXT.id,
          TEXT.status,
          TEXT.amount,
          TEXT.email,
          { name: TEXT.liability, class: 'text-center' },
          TEXT.date,
          { name: TEXT.actions, class: 'text-right' },
        ]}
        pagination={pagination}
        gridClassName="grid-7-with-id"
        onClickPerPage={onChangePerPage}
        onPageChange={onChangePage}
      >
        {!!data.length &&
          data.map((item, idx) => {
            const dataIndexNumber = data.length - 1 === idx ? 'last' : idx;

            return (
              <Fragment key={item.id}>
                <Link
                  data-index-number={dataIndexNumber}
                  className="underline text-javelin-gray-dark text-xs py-3 px-5"
                  to={`/transactions/${item.id}`}
                >
                  {item.id}
                </Link>
                <span
                  data-index-number={dataIndexNumber}
                  className="text-javelin-gray-dark text-xs py-3 pl-5"
                >
                  <StatusBadge status={item.status} className="ml-0" />
                </span>
                <span
                  data-index-number={dataIndexNumber}
                  className="text-javelin-gray-dark text-xs py-3 px-5 font-bold"
                >
                  {item.amountToDisplay}
                </span>
                <span
                  data-index-number={dataIndexNumber}
                  className="text-javelin-gray-dark text-xs py-3 px-5 truncate"
                >
                  {item.shopper_email || '-'}
                </span>
                <span
                  data-index-number={dataIndexNumber}
                  className="text-javelin-gray-dark text-xs py-3 px-5 text-center"
                >
                  {item.liability_shift ? (
                    <FontAwesomeIcon
                      icon="check"
                      size="lg"
                      className="text-green-lowRisk my-auto p-1 bg-green-lowRisk bg-opacity-20 rounded-full"
                    />
                  ) : (
                    <FontAwesomeIcon
                      icon="times"
                      size="lg"
                      className="fa-w-16 p-0.5 text-javelin-danger bg-javelin-danger bg-opacity-20 text-opacity-50 rounded-full"
                    />
                  )}
                </span>
                <span
                  data-index-number={dataIndexNumber}
                  className="text-javelin-gray-dark text-xs py-3 px-5"
                >
                  {format(new Date(item.created_at), MONTH_DAY_HOUR) || '-'}
                </span>
                <span
                  data-index-number={dataIndexNumber}
                  className="text-javelin-gray-dark text-xs relative py-3 text-right pr-5"
                >
                  <TransactionActions
                    onClickOpen={() => toggleActiveItem(item.id)}
                    isOpen={activeItem === item.id}
                    onClose={() => setActiveItem(null)}
                    transaction={item}
                    position={idx <= data.length - 3 ? 'bottom' : 'top'}
                  />
                </span>
              </Fragment>
            );
          })}
      </Table>
    </DashboardContainer>
  );
};

export default Transactions;
